import {
  ArrowDownUp,
  CalendarDays,
  CirclePlus,
  Contact,
  FilePen,
  FilePenLine,
  Home,
  LibraryBig,
  List,
  PlusCircle,
  Quote,
  Search,
  Tickets,
  UserCheck,
  Users,
} from "lucide-react"

import { WORKSPACE_OWNERS } from "@/components/workspace/definition"
import {
  defineWorkspacePages,
  workspacePageChrome,
} from "@/components/workspace/page-chrome"
import {
  editEducatorsTeacherClass,
  editEducatorsTeacherClassTeacherClassListingAvailability,
  editEducatorsTeacherProfile,
  editEducatorsTeacherSpeciality,
  editEducatorsTeacherTeam,
  editEducatorsTeacherTeamTeamTimetable,
  educatorsTeacherClassGoLive,
  educatorsTeacherClassStudent,
  educatorsTeacherClassStudents,
  educatorsTeacherClassTransfers,
  educatorsTeacherClasses,
  educatorsTeacherFeedback,
  educatorsTeacherReliefBookingStudent,
  educatorsTeacherReliefBookingStudents,
  educatorsTeacherReliefBookings,
  educatorsTeacherReliefListingRequests,
  educatorsTeacherReliefListings,
  educatorsTeacherSpecialities,
  educatorsTeacherTeamInvitations,
  educatorsTeacherTeamMembers,
  educatorsTeacherTeamTeamApplications,
  educatorsTeacherTeams,
  educatorsTeacherTimetable,
  educatorsTeacherWorkspace,
  newEducatorsTeacherClass,
  newEducatorsTeacherReliefListing,
  newEducatorsTeacherSpeciality,
  newEducatorsTeacherTeam,
} from "@/routes"

// The Education pages, keyed by Inertia component name, with the static
// facts the shell reads before a page's code runs: its layout, and for a page
// with the band the trail its chrome walks from the Workspace root. Every one
// is in the workspace of the teacher its route names. A page under a record
// goes through that record's page, which names the record's type for its
// "Name (Type)" crumb. An href is built from the current page's path params,
// so a record page's href names the key its child pages carry (classId, not
// id). The pages opened in a sheet are bare: they draw their own body and
// walk no trail.
export const educatorPages = defineWorkspacePages({
  "educators/timetables/show": {
    layout: "editor",
    owner: WORKSPACE_OWNERS.teacher,
    icon: CalendarDays,
    title: "timetables:edit.header",
    href: ({ teacherId }) => educatorsTeacherTimetable({ teacherId }).url,
  },
  "educators/feedbacks/show": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: Quote,
    title: "teacher_feedbacks:show.title",
    href: ({ teacherId }) => educatorsTeacherFeedback({ teacherId }).url,
  },
  "educators/profiles/edit": {
    layout: "editor",
    owner: WORKSPACE_OWNERS.teacher,
    icon: FilePen,
    title: "listing_profiles:edit.title",
    href: ({ teacherId }) => editEducatorsTeacherProfile({ teacherId }).url,
  },

  "educators/specialities/index": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: List,
    title: "specialities:index.title",
    href: ({ teacherId }) => educatorsTeacherSpecialities({ teacherId }).url,
  },
  "educators/specialities/new": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: CirclePlus,
    title: "specialities:new.title",
    href: ({ teacherId }) => newEducatorsTeacherSpeciality({ teacherId }).url,
    parent: "educators/specialities/index",
  },
  "educators/specialities/new_data_driven": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: CirclePlus,
    title: "specialities:new.title",
    href: ({ teacherId }) => newEducatorsTeacherSpeciality({ teacherId }).url,
    parent: "educators/specialities/index",
  },
  "educators/specialities/edit": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: FilePenLine,
    title: "specialities:edit.title",
    href: ({ teacherId, specialityId }) =>
      editEducatorsTeacherSpeciality({ teacherId, id: specialityId }).url,
    parent: "educators/specialities/index",
  },
  "educators/specialities/edit_data_driven": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: FilePenLine,
    title: "specialities:edit.title",
    href: ({ teacherId, specialityId }) =>
      editEducatorsTeacherSpeciality({ teacherId, id: specialityId }).url,
    parent: "educators/specialities/index",
  },

  "educators/classes/index": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: List,
    title: "classes:index.title",
    href: ({ teacherId }) => educatorsTeacherClasses({ teacherId }).url,
  },
  "educators/classes/new": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: PlusCircle,
    title: "classes:new.page_title",
    href: ({ teacherId }) => newEducatorsTeacherClass({ teacherId }).url,
    parent: "educators/classes/index",
  },
  "educators/classes/edit": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: FilePenLine,
    title: "custom_classes:edit.page_title",
    href: ({ teacherId, classId }) =>
      editEducatorsTeacherClass({ teacherId, id: classId }).url,
    parent: "educators/classes/index",
    record: "classes:record",
  },
  "educators/classes/go_lives/show": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: LibraryBig,
    title: "go_live:show.title",
    href: ({ teacherId, classId }) =>
      educatorsTeacherClassGoLive({ teacherId, classId }).url,
    parent: "educators/classes/edit",
  },
  "educators/classes/students/index": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: Users,
    title: "classes:students.title",
    href: ({ teacherId, classId }) =>
      educatorsTeacherClassStudents({ teacherId, classId }).url,
    parent: "educators/classes/edit",
  },
  "educators/classes/students/show": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: Search,
    title: "students:show.title",
    href: ({ teacherId, classId, id }) =>
      educatorsTeacherClassStudent({ teacherId, classId, id }).url,
    parent: "educators/classes/students/index",
  },
  "educators/classes/transfers/index": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: ArrowDownUp,
    title: "class_membership_requests:index.title",
    href: ({ teacherId, classId }) =>
      educatorsTeacherClassTransfers({ teacherId, classId }).url,
    parent: "educators/classes/edit",
  },
  "educators/classes/teacher_class_listing_availabilities/edit": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: FilePenLine,
    title: "teacher_class_listing_availabilities:edit.page_title",
    href: ({ teacherId, classId }) =>
      editEducatorsTeacherClassTeacherClassListingAvailability({
        teacherId,
        classId,
      }).url,
    parent: "educators/classes/edit",
  },

  "educators/teams/index": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: Users,
    title: "teams:index.title",
    href: ({ teacherId }) => educatorsTeacherTeams({ teacherId }).url,
  },
  "educators/teams/new": {
    layout: "editor",
    owner: WORKSPACE_OWNERS.teacher,
    icon: CirclePlus,
    title: "teams:new.title",
    href: ({ teacherId }) => newEducatorsTeacherTeam({ teacherId }).url,
    parent: "educators/teams/index",
  },
  "educators/teams/edit": {
    layout: "editor",
    owner: WORKSPACE_OWNERS.teacher,
    icon: FilePen,
    title: "teams:edit.title",
    href: ({ teacherId, teamId }) =>
      editEducatorsTeacherTeam({ teacherId, id: teamId }).url,
    parent: "educators/teams/index",
    record: "teams:record",
  },
  "educators/teams/members/index": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: Users,
    title: "team_members:index.title",
    href: ({ teacherId, teamId }) =>
      educatorsTeacherTeamMembers({ teacherId, teamId }).url,
    parent: "educators/teams/edit",
  },
  "educators/teams/invitations/index": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: Users,
    title: "invitations:index.title",
    href: ({ teacherId, teamId }) =>
      educatorsTeacherTeamInvitations({ teacherId, teamId }).url,
    parent: "educators/teams/edit",
  },
  "educators/teams/team_applications/index": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: Users,
    title: "team_applications:index.title",
    href: ({ teacherId, teamId }) =>
      educatorsTeacherTeamTeamApplications({ teacherId, teamId }).url,
    parent: "educators/teams/edit",
  },
  "educators/teams/team_timetables/edit": {
    layout: "editor",
    owner: WORKSPACE_OWNERS.teacher,
    icon: CalendarDays,
    title: "timetables:edit.header",
    href: ({ teacherId, teamId }) =>
      editEducatorsTeacherTeamTeamTimetable({ teacherId, teamId }).url,
    parent: "educators/teams/edit",
  },

  "educators/relief/listings/index": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: Contact,
    title: "educator_relief_listings:index.title",
    href: ({ teacherId }) => educatorsTeacherReliefListings({ teacherId }).url,
  },
  "educators/relief/listings/new": {
    layout: "editor",
    owner: WORKSPACE_OWNERS.teacher,
    icon: UserCheck,
    title: "educator_relief_listings:new.title",
    href: ({ teacherId }) =>
      newEducatorsTeacherReliefListing({ teacherId }).url,
    parent: "educators/relief/listings/index",
  },
  "educators/relief/requests/index": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: Contact,
    title: "educator_relief_listings:requests.index.title",
    href: ({ teacherId, listingId }) =>
      educatorsTeacherReliefListingRequests({ teacherId, listingId }).url,
    parent: "educators/relief/listings/index",
  },
  "educators/relief/bookings/index": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: Tickets,
    title: "educator_relief_listings:bookings.index.title",
    href: ({ teacherId }) => educatorsTeacherReliefBookings({ teacherId }).url,
  },
  // The Booking record page: a booking shows nothing but its students, so
  // their list is the booking's page and a student goes through it.
  "educators/relief/students/index": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: Users,
    title: "educator_relief_listings:bookings.students.index.title",
    href: ({ teacherId, bookingId }) =>
      educatorsTeacherReliefBookingStudents({ teacherId, bookingId }).url,
    parent: "educators/relief/bookings/index",
    record: "educator_relief_listings:bookings.record",
  },
  "educators/relief/students/show": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: Search,
    title: "students:show.title",
    href: ({ teacherId, bookingId, id }) =>
      educatorsTeacherReliefBookingStudent({ teacherId, bookingId, id }).url,
    parent: "educators/relief/students/index",
  },

  // The teacher's workspace, the root every trail above starts from.
  "educators/workspaces/show": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.teacher,
    icon: Home,
    title: "nav:teacher_workspace_title",
    href: ({ teacherId }) => educatorsTeacherWorkspace({ teacherId }).url,
    root: true,
  },

  "educators/invitations/invitations/new": { layout: "bare" },
  "educators/invitations/invitations/show": { layout: "bare" },
  "educators/invitations/responses/new": { layout: "bare" },
  "educators/invitations/responses/show": { layout: "bare" },
  "educators/relief/applications/edit": { layout: "bare" },
  "educators/relief/applications/new": { layout: "bare" },
  "educators/relief/applications/show": { layout: "bare" },
  "educators/relief/invitation_responses/new": { layout: "bare" },
  "educators/relief/invitation_responses/show": { layout: "bare" },
  "educators/relief/invitations/new": { layout: "bare" },
  "educators/relief/invitations/show": { layout: "bare" },
  "educators/team_applications/team_applications/new": { layout: "bare" },
  "educators/team_applications/team_applications/show": { layout: "bare" },
})

export const EducatorPageChrome = workspacePageChrome(educatorPages)
